"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchDropdown from "@/components/booking/search/SearchDropdown";
import { PORTAL_ROUTES } from "@/lib/routing/portal-routes";

interface NavBookingSearchProps {
  venueId: string;
  portal: "superadmin" | "booking";
  /** Full-width box for a mobile menu, instead of the top bar layout. */
  inline?: boolean;
  /** Called after a booking is picked, e.g. to close a mobile menu. */
  onSelect?: () => void;
}

/**
 * The booking calendar's search, in the top menu, so staff can find a
 * booking without opening the calendar first. Searches the selected venue
 * and opens the picked booking in the same tab. In the top bar it is a box
 * on wide screens and a search icon that opens the box below that.
 */
export function NavBookingSearch({
  venueId,
  portal,
  inline = false,
  onSelect,
}: NavBookingSearchProps) {
  const router = useRouter();
  const pathname = usePathname();
  // Separate queries, so the box hidden below xl doesn't also search
  // while the panel is used.
  const [searchQuery, setSearchQuery] = useState("");
  const [panelQuery, setPanelQuery] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const bookingsPath = PORTAL_ROUTES[portal].venue(venueId).bookings;

  useEffect(() => {
    if (!panelOpen) return;
    panelRef.current?.querySelector("input")?.focus();
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === "Escape"
          : !panelRef.current?.contains(event.target as Node)
      ) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [panelOpen]);

  // The calendar has its own search box.
  if (pathname === bookingsPath) return null;

  const search = (className: string, inPanel = false) => (
    <SearchDropdown
      // It loads the venue's bookings once; a new venue needs a fresh load.
      key={venueId}
      searchQuery={inPanel ? panelQuery : searchQuery}
      onSearchChange={inPanel ? setPanelQuery : setSearchQuery}
      bookings={[]}
      venueId={venueId}
      onBookingSelect={(bookingId) => {
        setPanelOpen(false);
        onSelect?.();
        router.push(`${bookingsPath}/${bookingId}`);
      }}
      className={className}
    />
  );

  if (inline) return search("w-full");

  return (
    <>
      <div className="hidden xl:block">{search("w-72")}</div>
      <div ref={panelRef} className="relative xl:hidden">
        <Button
          variant={panelOpen ? "secondary" : "ghost"}
          size="icon"
          className="h-10 w-10"
          title="Search bookings"
          aria-label="Search bookings"
          aria-expanded={panelOpen}
          onClick={() => setPanelOpen((open) => !open)}
        >
          <Search className="h-5 w-5" />
        </Button>
        {panelOpen && (
          <div className="absolute right-0 top-full z-50 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-md border bg-background p-2 shadow-lg">
            {search("w-full", true)}
          </div>
        )}
      </div>
    </>
  );
}
